import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ArrayService, LogService, NotificationService, PaymentService, ValueService, UserService, StripeService } from '@app/services';
import { CardService } from '@app/services/card.service';
import { Observable, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { StripeSource } from '../../models';
import { StripeFormComponent } from '../stripe-form/stripe-form.component';
import { ActivatedRoute, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { PaymentMethodHorizontalComponent } from '../../common-components/payment-method-horizontal/payment-method-horizontal.component';
import { NgClass } from '@angular/common';

@Component({
    selector: "stripe-wallet",
    templateUrl: "./stripe-wallet.component.html",
    styleUrls: ["./stripe-wallet.component.less"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass, PaymentMethodHorizontalComponent, StripeFormComponent]
})
export class StripeWalletComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'StripeWalletComponent';
  private _useNewCard: boolean = false;

  private _selectedSource: any;
  private _sources: any[];

  /**
   * Private field for verbose logging
   */
  private _verboseLogging = false;

  /**
   * Private subscription for router events
   */
  private routerEvent$: Subscription;

  private isLoggedInSubscription: Subscription;

  waiting = false;

  wasLoggedIn: boolean;
  showCards = false;

  @Input() disabled = false;
  @Input() errorMessage: string;
  @Output() canCaptureChange = new EventEmitter<boolean>();
  @Output() sourceChange = new EventEmitter<StripeSource>();
  @Output() newCardAdd = new EventEmitter();
  @Output() newCardAdded = new EventEmitter();

  /**
   * Flag indicating if the Choose Payment Method is closed when payment method is chosen
   */
  @Input() closeOnSelect = true;

  /**
   * An event for whenever changing is finished, regardless of whether cards were addded or selected.
   */
  @Output() changeFinished = new EventEmitter();

  @Input() title = 'Payment Method';

  /**
   * Stripe Form
   */
  @ViewChild(StripeFormComponent) stripeForm: StripeFormComponent;

  constructor(
    private stripeService: StripeService,
    readonly userService: UserService,
    private cardService: CardService,
    private notify: NotificationService,
    private changeDetector: ChangeDetectorRef,
    private paymentService: PaymentService,
    private log: LogService,
    private arrayHelper: ArrayService,
    private router: Router,
    private route: ActivatedRoute,
    private valueService: ValueService
  ) {
    this.subscribeRouterEvents();
  }

  public get selectedSource(): any {
    return this._selectedSource;
  }
  public set selectedSource(v: any) {
    this._selectedSource = v;

    if (this.verboseLogging) {
      console.log(`${this._componentName} selectedSource set to:`, v);
    }
  }

  public get sources(): any[] {
    return this._sources;
  }
  public set sources(v: any[]) {
    this._sources = v;
    if (this.verboseLogging) {
      console.log(`${this._componentName} set sources to: `, v);
    }
  }

  /**
   * Gets the default card from the sources array
   */
  public getDefaultCard() {
    if (this.sources && this.sources.length > 0) {
      return this.sources.find((x) => x.isDefault);
    }
    return null;
  }

  ngOnInit() {
    this.isLoggedInSubscription = this.userService.isLoggedIn$.pipe(distinctUntilChanged()).subscribe(() => {
      this.startup();
    });
    this.startup();
  }

  ngOnDestroy() {
    if (this.routerEvent$) {
      this.routerEvent$.unsubscribe();
    }

    if (this.isLoggedInSubscription) {
      this.isLoggedInSubscription.unsubscribe();
    }
  }

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging || this._verboseLogging;
  }

  /**
   * A flag to control the visibility of the stripe-form for addign a new card.  Also used by
   * the Stripe card capture workaround implemented in 12/2020
   */
  public get useNewCard(): boolean {
    return this._useNewCard;
  }

  @Input()
  public set useNewCard(v: boolean) {
    // console.log(`${this._componentName}.useNewCard setter v=`, v);
    this._useNewCard = v;
  }

  /**
   * Property to control the disabled state of the Add Card button
   */
  public get addCardEnabled(): boolean {
    return !this.waiting && this.useNewCard;
  }

  /**
   *
   */
  public onChangePaymentMethodClick() {
    this.showCards = true;
  }

  public onCloseClick() {
    this.closeChoosePaymentMethod();
  }

  /**
   * Closes the choose payment method div
   */
  private closeChoosePaymentMethod() {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.closeChoosePaymentMethod()`);
    }

    this.showCards = false;
    this.changeFinished.emit();
  }

  showNewCard() {
    this.useNewCard = true;
    this.newCardAdd.emit();
  }

  startup() {
    if (this.wasLoggedIn !== undefined && this.wasLoggedIn === this.userService.isLoggedIn) {
      return;
    }
    this.wasLoggedIn = this.userService.isLoggedIn;

    if (!this.userService.isLoggedIn) {
      this.useNewCard = true;
      this.canCaptureChange.emit(true);
      return;
    }

    this.stripeService.getAccountSources().subscribe((sources) => {
      this.sources = this.arrayHelper.orderByDescending(sources, 'isDefault');

      if (this.sources && this.sources[0]) {
        this.useNewCard = false;
        // preselect first (will be default) source.
        this.selectedSource = sources[0];
        this.sourceChange.emit(this.selectedSource);
        this.canCaptureChange.emit(true);
      } else {
        this.useNewCard = true;
        this.canCaptureChange.emit(true);
      }
      this.changeDetector.detectChanges();
    });
  }

  /**
   * Render expiration label as MM/YY.
   * Validates inputs and avoids deprecated String.substr usage.
   */
  renderExpLabel(source?: StripeSource): string {
    if (!source) {
      return '';
    }

    const { expMonth, expYear } = source;

    if (expMonth === undefined || expMonth === null || expYear === undefined || expYear === null) {
      return '';
    }

    // Ensure month is two digits (01..12) and year is last two digits
    const mm = String(expMonth).padStart(2, '0');
    const yy = String(expYear).slice(-2); // use slice instead of deprecated substr

    return `${mm}/${yy}`;
  }

  public onSourceClick(source: StripeSource) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.onSourceClick() source=`, source);
    }

    if (this.disabled) {
      return;
    }

    this.selectSource(source);
  }

  private selectSource(source: StripeSource) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.selectSource source=`, source);
    }

    if (source) {
      const selectedSourceId = this.getSelectedSourceId();
      if (this.verboseLogging) {
        console.log(`${this._componentName}.selectSource selectedSourceId=`, selectedSourceId);
      }

      const sourceId = this.paymentService.getSourceId(source);
      if (this.verboseLogging) {
        console.log(`${this._componentName}.selectSource sourceId=`, sourceId);
      }

      if (this.selectedSource !== undefined && selectedSourceId === sourceId) {
        // there's no actual change.
        if (this.verboseLogging) {
          console.log(`${this._componentName}.selectSource No Change`);
        }
      } else {
        // select source.
        this.selectedSource = source;
        this.sourceChange.emit(source);
        this.canCaptureChange.emit(true);
      }

      this.changeDetector.detectChanges();

      if (this.closeOnSelect) {
        this.closeChoosePaymentMethod();
      }
    } else {
      console.warn(`${this._componentName}.selectSource - empty source`);
    }
  }

  /**
   * Gets the Stripe Source ID from the selected source
   */
  private getSelectedSourceId() {
    if (this.selectedSource !== undefined && this.selectedSource) {
      return this.paymentService.getSourceId(this.selectedSource);
    } else {
      return null;
    }
  }

  /**
   * onAddCardClick
   */
  public onAddCardClick(evt: MouseEvent) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.onAddCardClick()`, evt);
    }
    this.addNewCard();
  }

  /**
   * Handles when the user is adding in a new payment method, but then cancels.
   */
  public onCancelAddNewCardClick() {
    this.useNewCard = false;

    this.closeChoosePaymentMethod();
  }

  /**
   * Tokenizes the card entered in the new-card form with Stripe and saves it to the user's wallet.
   * Emits the saved source; use paymentService.getSourceId() to get the id to purchase with.
   */
  public captureAndAddCard(): Observable<StripeSource> {
    return this.stripeForm.capture().pipe(switchMap((card: StripeSource) => this.cardService.add(card)));
  }

  /**
   * Add a new card
   */
  public addNewCard() {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.addNewCard()`);
    }

    this.waiting = true;

    this.captureAndAddCard()
      .pipe(
        catchError((err) => {
          this.notify.error(err.message || 'An error occured while adding card to wallet.', null);
          console.error(err);
          this.waiting = false;
          return observableThrowError(err);
        })
      )
      .subscribe({
        next: (result: any) => {
          if (result) {
            this.reloadCards(result.stripeSourceId, false);
          } else {
            this.notify.error('An error occured while adding card to wallet.', 'Payment Method Error');
            this.waiting = false;
          }
        },
        error: () => {
          // Error already handled in catchError
        }
      });
  }

  /**
   * Reload the cards in the stripe wallet and the source
   * @param selectedStripeSourceId
   */
  reloadCards(selectedStripeSourceId: string, useNewCard: boolean) {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.reloadCards() useNewCard=`, useNewCard);
    }

    this.stripeService.getAccountSources().subscribe(
      (sources) => {
        this.sources = sources;

        const s = sources.find((x) => (x as any).sourceId === selectedStripeSourceId);

        if (s) {
          this.selectedSource = s;
          this.sourceChange.emit(this.selectedSource);
        } else {
          if (sources.length > 0) {
            this.selectedSource = sources[0];
            this.sourceChange.emit(this.selectedSource);
          }
        }

        this.sourceChange.emit(this.selectedSource);

        this.useNewCard = useNewCard;

        this.changeDetector.detectChanges();
        //this.newCardAdd.emit();

        if (this.closeOnSelect) {
          this.closeChoosePaymentMethod();
        }

        // console.log(`${this._componentName}.reloadCards() complete   sources=`, this.sources);
        // console.log(`${this._componentName}.reloadCards() complete   selectedSource=`, this.selectedSource);
      },
      (err) => {
        this.notify.error('An error occured when loading cards into wallet.', null);
        console.error(err);
      },
      () => {
        this.waiting = false;
      }
    );
  }

  brandIcon(brand) {
    return this.stripeService.brandIconCSSClass(brand);
  }

  panelClass(source: any) {
    return this.selectedSource && source.sourceId === this.selectedSource.sourceId ? 'credit-card credit-card-selected' : 'credit-card';
  }

  capture(): Observable<StripeSource> {
    if (this.useNewCard) {
      // capture from new card form.

      var source = this.stripeForm.capture();

      // .map(s=> {
      //   // console.log('Capture Source', s);
      //   // 
      //   return s;
      // });

      return source;
    } else if (this.selectedSource) {
      // return selected source.
      return observableOf(this.selectedSource);
    } else {
      return observableThrowError(new Error());
    }
  }

  public onHeadingClick(evt: MouseEvent) {
    if (evt.altKey) {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.onHeadingClick() - showCards=${this.showCards}`);
        console.log(`${this._componentName}.onHeadingClick() - useNewCard=${this.useNewCard}`);
        console.log(`${this._componentName}.onHeadingClick() - selectedSource=`, this.selectedSource);
      }
    }
  }

  /**
   * isSourceChecked
   */
  public isSourceChecked(source: any) {
    // if (this.verboseLogging) {
    //   console.log(`${this._componentName}.isSourceChecked source=`, source);
    // }

    if (this.selectedSource && source) {
      if (this.selectedSource.sourceId === source.sourceId) {
        return true;
      }
    }

    return false;
  }

  create() {
    if (this.useNewCard) {
      this.capture().subscribe((card) => {
        this.cardService.add(card).subscribe(
          (source) => {
            if (this.verboseLogging) {
              console.log('added stripe source: ', source);
            }
          },
          (error) => {
            console.error(error);
            this.notify.error('An error occured while adding card.', null);
          },
          () => {}
        );
      });
    }
  }

  /**
   * Subscribes to Router Events and sets the _verboseLogging flag
   */
  private subscribeRouterEvents() {
    this.routerEvent$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        console.log(`${this._componentName}.CTOR router.events NavigationEnd:`, event);

        console.log(
          `${this._componentName}.CTOR router.events NavigationEnd route.snapshot.queryParamMap=`,
          this.route.snapshot.queryParamMap
        );

        if (this.route.snapshot.queryParamMap.has('verboseLogging')) {
          if (this.valueService.getBoolean(this.route.snapshot.queryParamMap.get('verboseLogging')) == true) {
            this._verboseLogging = true;
          } else {
            this._verboseLogging = false;
          }
        }
      }
    });
  }
}
